import { ReactElement } from 'react'
import { OrderResponseDetail } from '../../../../../../../src/api/cart/interfaces'
import { VehicleInfoProps } from '../../../../../../../src/features/SummaryPanel/Interfaces'
import Header from './header'
import styled from 'styled-components'
import flatten from 'lodash-es/flatten'
import Order from './order'
import { ISeller } from '../../../../../../../src/services/MultiSellerServicesProvider'
import { defaultTheme } from '../../../../../../../src/theme'
import { I18n } from 'common'
import {
  calcOrderSubTotal,
  getPartDetail,
} from '../../../../orderConfirmationUtils'
import { Price } from '../../../../../../../src/ui-components'
import Labor from './Labor'
import { getReceiptLaborItems } from '../getReceiptLaborItems'
import Specifications from './Specifications'
import ReceiptHeader from '../ReceiptHeader'

export interface Props {
  showYourCost: boolean
  orderDetails: OrderResponseDetail
  vehicleInfo: VehicleInfoProps
  sellersData: ISeller[]
  orderDate: string
  isFirstVehicle: boolean
  logoUrl: string
  displayExclTax?: boolean
}

const VehicleReceipt = (props: Props): ReactElement => {
  const {
    showYourCost,
    orderDetails,
    vehicleInfo,
    sellersData,
    orderDate,
    isFirstVehicle,
    logoUrl,
    displayExclTax,
  } = props
  const { comment, specifications } = orderDetails
  const laborItems = getReceiptLaborItems(orderDetails)

  const flattenedOrders = flatten(
    orderDetails.locations.map((location) =>
      location.orders.map((order) => ({
        ...order,
        coverageKey: location.coverageKey ?? '',
        noteToStore: location?.noteToStore
          ? location?.noteToStore
          : orderDetails.noteToStore,
        poNumber: location?.poNumber
          ? location?.poNumber
          : orderDetails.poNumber,
        specifications: orderDetails.specifications,
        laborData: orderDetails.laborData,
      }))
    )
  )

  const cartTotal =
    orderDetails.vehicleSummary?.vehicleTotalCost ??
    calcOrderSubTotal(
      flatten(
        flattenedOrders.map((order) =>
          order.parts.map((part) => getPartDetail(part))
        )
      ),
      laborItems
    )

  return (
    <Wrapper $isFirstVehicle={isFirstVehicle}>
      {!isFirstVehicle && (
        <ReceiptHeader logoUrl={logoUrl} orderDate={orderDate} />
      )}
      <Header
        vehicleName={vehicleInfo.vehicleName}
        personalNote={comment}
        customerName={
          orderDetails?.locations[0]?.orders[0]?.deliveryDetail?.name
        }
        cartId={orderDetails.cartId}
        orderDate={orderDate}
      />

      {flattenedOrders.map((o) => (
        <Order
          key={`print_order_${o.orderNumber}`}
          order={o}
          {...o}
          sellersData={sellersData}
          showYourCost={showYourCost}
        />
      ))}

      {specifications && specifications.length > 0 && (
        <Specifications specifications={specifications} />
      )}

      {laborItems.length > 0 && <Labor laborItems={laborItems} />}

      {showYourCost && (
        <Footer>
          <FooterLine>
            <I18n name="cartTotal" />: <Price num={cartTotal} />
          </FooterLine>
          {displayExclTax && (
            <ExclTaxText>
              <I18n name="displayExclTax" />
            </ExclTaxText>
          )}
        </Footer>
      )}
    </Wrapper>
  )
}

interface WrapperProps {
  $isFirstVehicle: boolean
}

const Wrapper = styled.div<WrapperProps>`
  display: flex;
  flex-direction: column;
  gap: 3px;

  @media print {
    page-break-before: ${(p) => (p.$isFirstVehicle ? 'auto' : 'always')};
    break-before: ${(p) => (p.$isFirstVehicle ? 'auto' : 'page')};
  }
`

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: flex-end;
  color: ${defaultTheme.colors.white};
  background-color: ${defaultTheme.colors.black};
  font-weight: 700;
  font-size: 11pt;
  line-height: 14pt;
  padding: 4px 8px;
`

const FooterLine = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`

const ExclTaxText = styled.p`
  font-style: italic;
  font-size: 8pt;
  color: ${defaultTheme.colors.white};
  margin: 0;
  font-weight: 400;
`

export default VehicleReceipt
